import getConfig from 'next/config';
import React, { Component, createContext } from 'react';
import { ErrorType } from '../domain/entities/Auth';
import User from '../domain/entities/User';
import implAuthRepository, {
  CreateUserQuery,
  CreateUserWithResvQuery,
  LoginWithFbQuery,
} from '../infrastructure/repositories/AuthRepository';
import * as errors from '../infrastructure/repositories/exceptions/errors';
import implUserRepository, {
  UserRepository,
} from '../infrastructure/repositories/UserRepository';
import { getCookie } from '../utils/cookie';
import { newSecuredAxios } from '../utils/secured-axios';

const { publicRuntimeConfig } = getConfig();
const isDevelopment = publicRuntimeConfig.coubicEnv === 'development';

export interface UserContextState {
  request: {
    isCoubicApp: boolean;
    isServer: boolean;
    host: string;
  };
  auth: {
    isLoggedIn: boolean;
    user: User | undefined;
    errors?: ErrorType[];
    login: (email: string, password: string) => Promise<boolean>;
    loginWithFb: (query: LoginWithFbQuery) => Promise<boolean>;
    createUserWithResv: (query: CreateUserWithResvQuery) => Promise<boolean>;
    createUser: (query: CreateUserQuery) => Promise<boolean>;
    logout: () => void;
  };
  session: string;
  token: string;
  requestId: string;
}

// required for initialization
export interface InputContext {
  coubicUAType: string;
  isServer: boolean;
  user?: User;
  host: string;
  requestId: string;
  session: string;
  token: string;
}

// tslint:disable:no-any
export const makeInputContext = async (
  isServer: boolean,
  requestId: string,
  req?: any,
): Promise<InputContext> => {
  const uatype = req ? getCookie('uatype', req) : '';
  const token = req ? getCookie('token', req) : '';
  const protocol = isDevelopment ? 'http' : 'https';
  const host = req && req.headers ? `${protocol}://${req.headers.host}` : '';
  const session = req ? getCookie('cb', req) : '';
  const user =
    token || session
      ? await new UserRepository(newSecuredAxios(session, token, requestId))
          .Me()
          .catch(err => {
            if (err instanceof errors.AuthenticationFailed) {
              return undefined;
            }

            throw err;
          })
      : undefined;

  return {
    coubicUAType: uatype,
    isServer,
    user,
    requestId,
    host: host || '',
    session,
    token,
  };
};

// initialize a state of user context
const initializeState = (
  ctx?: InputContext,
  login?: (email: string, passwd: string) => Promise<boolean>,
  loginWithFb?: (query: LoginWithFbQuery) => Promise<boolean>,
  createUserWithResv?: (query: CreateUserWithResvQuery) => Promise<boolean>,
  createUser?: (query: CreateUserQuery) => Promise<boolean>,
  logout?: () => void,
): UserContextState => {
  const uatype = ctx ? ctx.coubicUAType : '';
  const user = ctx ? ctx.user : undefined;

  return {
    request: {
      isCoubicApp: uatype === 'coubook-ios' || uatype === 'coubook-android',
      isServer: ctx ? ctx.isServer : false,
      host: ctx ? ctx.host : '',
    },
    auth: {
      isLoggedIn: !!user,
      user,
      login: login ? login : async (_e: string, _p: string) => false,
      loginWithFb: loginWithFb
        ? loginWithFb
        : async (_q: LoginWithFbQuery) => false,
      createUserWithResv: createUserWithResv
        ? createUserWithResv
        : async (_q: CreateUserWithResvQuery) => false,
      createUser: createUser
        ? createUser
        : async (_q: CreateUserQuery) => false,
      logout: logout ? logout : () => undefined,
    },
    session: ctx ? ctx.session : '',
    token: '',
    requestId: ctx ? ctx.requestId : '',
  };
};

export const UserContext = createContext<UserContextState>(initializeState());

export class UserContextProvider extends Component<
  InputContext,
  UserContextState
> {
  public state: UserContextState;

  constructor(props: InputContext) {
    super(props);

    this.state = initializeState(
      props,
      this.login,
      this.loginWithFb,
      this.createUserWithResv,
      this.createUser,
      this.logout,
    );
  }

  private login = async (email: string, password: string): Promise<boolean> => {
    const result = await implAuthRepository.Login(email, password);
    if (result.errors) {
      this.setState({
        ...this.state,
        auth: { ...this.state.auth, isLoggedIn: false, errors: result.errors },
      });
      throw Error;
    }
    const user = await implUserRepository.Me();
    this.setState({
      ...this.state,
      auth: { ...this.state.auth, isLoggedIn: true, user },
    });
    return this.state.auth.isLoggedIn;
  };

  private loginWithFb = async (query: LoginWithFbQuery): Promise<boolean> => {
    const result = await implAuthRepository.LoginWithFb(query);
    if (result.errors) {
      this.setState({
        ...this.state,
        auth: { ...this.state.auth, isLoggedIn: false, errors: result.errors },
      });
      throw Error;
    }
    const user = await implUserRepository.Me();
    this.setState({
      ...this.state,
      auth: { ...this.state.auth, isLoggedIn: true, user },
    });
    return this.state.auth.isLoggedIn;
  };

  private createUser = async (query: CreateUserQuery): Promise<boolean> => {
    const result = await implAuthRepository.CreateUser(query);
    if (result.errors) {
      this.setState({
        ...this.state,
        auth: { ...this.state.auth, isLoggedIn: false, errors: result.errors },
      });
      throw Error;
    }
    const user = await implUserRepository.Me();
    this.setState({
      ...this.state,
      auth: { ...this.state.auth, isLoggedIn: true, user },
    });
    return this.state.auth.isLoggedIn;
  };

  private createUserWithResv = async (
    query: CreateUserWithResvQuery,
  ): Promise<boolean> => {
    const result = await implAuthRepository.CreateUserWithResv(query);
    if (result.errors) {
      this.setState({
        ...this.state,
        auth: { ...this.state.auth, isLoggedIn: false, errors: result.errors },
      });
      throw Error;
    }
    const user = await implUserRepository.Me();
    this.setState({
      ...this.state,
      auth: { ...this.state.auth, isLoggedIn: true, user },
    });
    return this.state.auth.isLoggedIn;
  };

  private logout = async () => {
    try {
      await implAuthRepository.Logout();
    } catch (err) {
      throw err;
    }
    this.setState({
      ...this.state,
      auth: { ...this.state.auth, isLoggedIn: false, user: undefined },
    });
  };

  public render() {
    return (
      <UserContext.Provider value={this.state}>
        {this.props.children}
      </UserContext.Provider>
    );
  }
}

// then make a consumer which will surface it
export const UserContextConsumer = UserContext.Consumer;
